import type { MaybeRefOrGetter } from "vue"
import type { Album } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { ref, toValue } from "vue"
import { GET_ALBUM_QUERY_KEY, LIST_ALBUMS_QUERY_KEY } from "@/api"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { useModal } from "@/composables/useModal"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { GuestUnlockAlbumModalKey } from "@/symbols"

export interface UseGuestAlbumUnlockOptions {
  eventVaultId: MaybeRefOrGetter<number | undefined>
  isHost: MaybeRefOrGetter<boolean>
  selectAlbumById: (id: number) => void
  scrollToMainSection?: (behavior?: ScrollBehavior) => void
}

export function useGuestAlbumUnlock({
  eventVaultId,
  isHost,
  selectAlbumById,
  scrollToMainSection,
}: UseGuestAlbumUnlockOptions) {
  const queryClient = useQueryClient()
  const eventVaultStore = useEventVaultStore()
  const { toggle: toggleGuestUnlockAlbumModal } = useModal(GuestUnlockAlbumModalKey)

  const unlockPendingAlbum = ref<Album | undefined>()

  function clearUnlockPending() {
    unlockPendingAlbum.value = undefined
  }

  function openUnlockModalForAlbum(album: Album | undefined) {
    if (album?.id === undefined)
      return

    unlockPendingAlbum.value = album
    toggleGuestUnlockAlbumModal()
  }

  function onAlbumSelect(album: Album) {
    const id = album.id
    if (id === undefined)
      return

    if (album.isLocked && !toValue(isHost)) {
      openUnlockModalForAlbum(album)
      return
    }

    selectAlbumById(id)
  }

  async function onGuestAlbumUnlocked() {
    const vaultId = toValue(eventVaultId)
    const unlockedAlbumId = unlockPendingAlbum.value?.id

    if (!vaultId || unlockedAlbumId === undefined)
      return

    await queryClient.invalidateQueries({ queryKey: [LIST_ALBUMS_QUERY_KEY, vaultId] })
    await queryClient.invalidateQueries({ queryKey: [GET_ALBUM_QUERY_KEY, vaultId, unlockedAlbumId] })
    await queryClient.invalidateQueries({ queryKey: ["listMedia"] })

    unlockPendingAlbum.value = undefined

    selectAlbumById(unlockedAlbumId)
    eventVaultStore.setAlbumId(unlockedAlbumId)
    emitSelectedAlbum(unlockedAlbumId)
    scrollToMainSection?.()
  }

  return {
    unlockPendingAlbum,
    onAlbumSelect,
    onGuestAlbumUnlocked,
    openUnlockModalForAlbum,
    clearUnlockPending,
  }
}
